/**
 * Local stub for `flagsmith/react`.
 *
 * Aliased in config-overrides.js so every `import ... from "flagsmith/react"`
 * across the codebase resolves here. The result: every feature flag reports
 * `enabled: true`, no matter what the Flagsmith API returns.
 *
 * Why: we don't want UI features hidden by a remote flag service. Toggling
 * everything on at the call-site level would mean editing dozens of files;
 * one alias handles them all.
 *
 * Reverting: delete the `flagsmith/react` entry in config-overrides.js' alias
 * map (or remove this file). All call sites then read from the real Flagsmith
 * client again.
 */

import React from "react";

/** All flags appear enabled. `value` stays null so any consumer that reads
 *  a string/number payload (rare here) gets a benign default. */
export function useFlags(flagNames) {
  const out = {};
  if (Array.isArray(flagNames)) {
    for (const name of flagNames) {
      if (typeof name === "string" && name.length > 0) {
        out[name] = { enabled: true, value: null };
      }
    }
  }
  return out;
}

/** Loading state is never pending — we have no API to wait on. */
export function useFlagsmithLoading() {
  return {
    isLoading: false,
    isFetching: false,
    error: null,
    source: "DEFAULT"
  };
}

/** Provider is a pass-through. Accepts the same props as the real one so
 *  the existing `<FlagsmithProvider options={...} flagsmith={...}>` call
 *  in routes.js keeps compiling. */
export function FlagsmithProvider({ children }) {
  return <>{children}</>;
}

const stub = { useFlags, useFlagsmithLoading, FlagsmithProvider };
export default stub;
